import React, { useEffect, useMemo, useState } from "react";
import { Rocket, Loader2, CheckCircle2, XCircle, User, Youtube, Music, Search, Upload, ExternalLink, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * AdminBulkSeedPanel
 * Un seul textarea pour seeder 20+ artistes en 1 opération (YouTube ou TikTok).
 * Format par ligne :
 *   @handle | category | genre | city
 *   (ou juste "@handle")
 */
export const AdminBulkSeedPanel = () => {
  const [source, setSource] = useState("youtube"); // "youtube" | "tiktok"
  const [raw, setRaw] = useState("");
  const [maxPerChannel, setMaxPerChannel] = useState(10);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const items = useMemo(() => {
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((l) => l && !l.startsWith("#"))
      .map((line) => {
        const parts = line.split("|").map((p) => p.trim());
        return {
          handle: parts[0],
          category: parts[1] || "music",
          genre: parts[2] || "",
          city: parts[3] || "",
        };
      })
      .filter((i) => i.handle);
  }, [raw]);

  const launch = async () => {
    if (items.length === 0) { toast.error("Colle au moins une ligne"); return; }
    setRunning(true);
    setResult(null);
    try {
      const endpoint = source === "tiktok"
        ? "/admin/users/bulk-from-tiktok"
        : "/admin/users/bulk-from-youtube";
      const payloadKey = source === "tiktok" ? "max_per_profile" : "max_per_channel";
      const { data } = await api.post(endpoint, {
        items,
        [payloadKey]: maxPerChannel,
      });
      setResult(data);
      const { users_created, videos_created, failed } = data.summary;
      toast.success(`${users_created} artiste${users_created > 1 ? "s" : ""} créé${users_created > 1 ? "s" : ""} · ${videos_created} vidéo${videos_created > 1 ? "s" : ""} importée${videos_created > 1 ? "s" : ""} 🚀`);
      if (failed > 0) toast.error(`${failed} handle${failed > 1 ? "s" : ""} non résolu${failed > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur seed");
    } finally {
      setRunning(false);
    }
  };

  const fillExample = () => {
    if (source === "tiktok") {
      setRaw([
        "# Format TikTok : @handle | categorie | genre | ville",
        "@rossymada | music | Salegy | Antananarivo",
        "@bigmjofficiel | music | Rap Gasy |",
        "@khaby.lame | humor | Sketch |",
      ].join("\n"));
    } else {
      setRaw([
        "# Format YouTube : @handle | categorie | genre | ville",
        "@BigMJofficiel | music | Rap Gasy | Antananarivo",
        "@Rossy | music | Salegy | Antananarivo",
        "@Arikaonline | music | Afrobeat |",
        "@MrBeast | other | | ",
      ].join("\n"));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-bulk-seed-panel">
      <div className="rounded-2xl border border-brand-red/25 bg-gradient-to-br from-brand-red/10 via-brand-orange/5 to-brand-yellow/5 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Rocket size={16} className="text-brand-red" />
          <p className="text-[10px] uppercase tracking-widest text-brand-red font-bold">Seed massif</p>
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-black mb-1">
          {source === "tiktok" ? "Import TikTok massif." : "Crée 20 artistes en 1 clic."}
        </h2>
        <p className="text-sm text-white/60 mb-4">
          {source === "tiktok"
            ? <>Choisis un artiste, colle la liste d'<strong className="text-brand-yellow">URLs de vidéos TikTok</strong> (une par ligne), clique Importer. Les <strong>titres + miniatures</strong> sont récupérés automatiquement via l'oEmbed officiel TikTok.</>
            : <>Colle des handles YouTube. Pour chacun : résolution du profil, <strong className="text-white">nom + avatar + bio + bannière</strong>, création de l'artiste, puis import des X dernières vidéos. Dédoublonnage automatique.</>}
        </p>

        {/* Source toggle */}
        <div className="flex gap-1 p-1 bg-black/40 rounded-xl border border-white/10 mb-4 max-w-sm" data-testid="bulk-source-toggle">
          <button
            type="button"
            onClick={() => { setSource("youtube"); setResult(null); }}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors ${
              source === "youtube" ? "bg-brand-red/20 text-brand-red" : "text-white/50 hover:text-white"
            }`}
            data-testid="bulk-source-youtube"
          >
            <Youtube size={13} /> YouTube
          </button>
          <button
            type="button"
            onClick={() => { setSource("tiktok"); setResult(null); }}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors ${
              source === "tiktok" ? "bg-brand-yellow/20 text-brand-yellow" : "text-white/50 hover:text-white"
            }`}
            data-testid="bulk-source-tiktok"
          >
            <Music size={13} /> TikTok
          </button>
        </div>

        <div className="flex items-center gap-3 mb-3">
          {source === "tiktok" ? (
            <span className="text-xs text-white/40">Colle les URLs TikTok (une par ligne) — les titres et miniatures sont récupérés automatiquement.</span>
          ) : (
            <>
              <button onClick={fillExample} className="text-xs text-brand-yellow hover:underline" data-testid="bulk-fill-example">
                Insérer un exemple
              </button>
              <span className="text-xs text-white/40 ml-auto" data-testid="bulk-items-count">
                {items.length} ligne{items.length > 1 ? "s" : ""} détectée{items.length > 1 ? "s" : ""}
              </span>
            </>
          )}
        </div>

        {source === "tiktok" ? (
          <TikTokBulkUrls />
        ) : (
          <>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={10}
              placeholder={"@BigMJofficiel | music | Rap Gasy | Antananarivo\n@Rossy | music | Salegy |\n@Arika | music | Afrobeat |\n# les lignes commençant par # sont ignorées"}
              className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand-red/60 resize-y"
              data-testid="bulk-textarea"
            />

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <label className="text-xs text-white/60 flex items-center gap-2">
                Max vidéos / chaîne :
                <input
                  type="number"
                  min="1" max="50"
                  value={maxPerChannel}
                  onChange={(e) => setMaxPerChannel(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
                  className="w-16 rounded-lg bg-white/[0.03] border border-white/10 px-2 py-1 text-sm focus:outline-none focus:border-brand-orange/60"
                  data-testid="bulk-max"
                />
              </label>
              <button
                onClick={launch}
                disabled={running || items.length === 0}
                className="btn-primary inline-flex items-center gap-2 ml-auto disabled:opacity-50"
                data-testid="bulk-launch"
              >
                {running
                  ? <><Loader2 size={14} className="animate-spin" /> Seed en cours…</>
                  : <><Rocket size={14} /> Lancer le seed {items.length > 0 && `(${items.length})`}</>}
              </button>
            </div>

            <p className="text-[10px] text-white/35 mt-2 leading-relaxed">
              ⏱️ Chaque handle prend ~2-3 secondes (résolution + scraping + RSS + inserts). Pour 20 handles : ~1 min total.
            </p>
          </>
        )}
      </div>

      {result && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="bulk-result">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <SumCard label="Traités"       value={result.summary.processed}      />
            <SumCard label="Artistes créés" value={result.summary.users_created} accent="text-emerald-400" />
            <SumCard label="Déjà existants" value={result.summary.users_existed} accent="text-white/60" />
            <SumCard label="Vidéos créées"  value={result.summary.videos_created} accent="text-brand-red" />
            <SumCard label="Échecs"         value={result.summary.failed}         accent="text-red-400" />
          </div>

          <h3 className="font-display text-sm font-black uppercase tracking-widest mb-3 text-white/70">Rapport détaillé</h3>
          <ul className="divide-y divide-white/5">
            {result.report.map((r, i) => (
              <li key={i} className="py-2.5 flex flex-wrap items-center gap-2 text-sm" data-testid={`bulk-row-${i}`}>
                <span className={`shrink-0 w-5 text-center ${r.status === "failed" ? "text-red-400" : "text-emerald-400"}`}>
                  {r.status === "failed" ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
                </span>
                <span className="font-mono text-xs text-white/50 shrink-0">{r.handle}</span>
                {r.channel_title && (
                  <span className="font-semibold flex items-center gap-1.5 min-w-0">
                    {source === "tiktok"
                      ? <Music size={12} className="text-brand-yellow shrink-0" />
                      : <Youtube size={12} className="text-brand-red shrink-0" />}
                    <span className="truncate">{r.channel_title}</span>
                  </span>
                )}
                {r.user_status && (
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    r.user_status === "created"
                      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                      : "bg-white/5 text-white/50 border border-white/10"
                  }`}>
                    <User size={9} className="inline mr-0.5" /> {r.user_status === "created" ? "créé" : "existant"}
                  </span>
                )}
                {r.avatar === "✓" && <span className="text-[10px] text-brand-yellow">avatar</span>}
                {r.bio && r.bio !== "—" && <span className="text-[10px] text-white/50" title={r.bio}>bio</span>}
                <span className="ml-auto text-xs">
                  {r.videos_created !== undefined && (
                    <>
                      <span className="text-emerald-400 font-bold">{r.videos_created}</span>
                      <span className="text-white/40"> créées</span>
                      {r.videos_skipped > 0 && (
                        <span className="text-white/40"> · {r.videos_skipped} skip</span>
                      )}
                    </>
                  )}
                  {r.error && <span className="text-red-400">{r.error}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const SumCard = ({ label, value, accent = "text-white" }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 p-3">
    <p className={`font-display text-3xl font-black leading-none ${accent}`}>{value ?? "—"}</p>
    <p className="text-[9px] uppercase tracking-widest text-white/50 mt-1">{label}</p>
  </div>
);

/**
 * TikTokBulkUrls — UI ultra-simple : picker artiste + textarea URLs + bouton Importer.
 * Appelle /admin/users/{id}/tracks/batch qui enrichit via oEmbed TikTok officiel.
 * Zéro bookmarklet, zéro console F12, zéro manipulation technique.
 */
const TikTokBulkUrls = () => {
  const [allArtists, setAllArtists] = useState([]);
  const [artistQ, setArtistQ] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [urlsText, setUrlsText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/users", { params: { role: "artist", limit: 1000 } })
      .then(({ data }) => setAllArtists(data))
      .catch(() => setAllArtists([]));
  }, []);

  const filteredArtists = useMemo(() => {
    const q = artistQ.trim().toLowerCase();
    if (!q) return allArtists.slice(0, 20);
    return allArtists.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [artistQ, allArtists]);

  const urlList = useMemo(() => {
    return urlsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.includes("tiktok.com"));
  }, [urlsText]);

  const launchImport = async () => {
    if (!selectedArtist) { toast.error("Choisis un artiste d'abord"); return; }
    if (urlList.length === 0) { toast.error("Colle au moins une URL TikTok"); return; }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/admin/users/${selectedArtist.user_id}/tracks/batch`,
        { urls: urlList, category: "music", genre: "" }
      );
      setResult(data);
      if (data.summary.succeeded > 0) {
        toast.success(`${data.summary.succeeded} vidéo${data.summary.succeeded > 1 ? "s" : ""} TikTok ajoutée${data.summary.succeeded > 1 ? "s" : ""} à ${selectedArtist.name} ✨`);
      }
      if (data.summary.failed > 0) toast.error(`${data.summary.failed} URL${data.summary.failed > 1 ? "s" : ""} non reconnue${data.summary.failed > 1 ? "s" : ""}`);
      if (data.summary.skipped > 0) toast(`${data.summary.skipped} déjà importée${data.summary.skipped > 1 ? "s" : ""} (ignorée${data.summary.skipped > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="tiktok-bulk-urls">
      {/* Extension download banner */}
      <div className="lg:col-span-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-wrap items-center gap-3" data-testid="chrome-ext-banner">
        <div className="text-3xl">🧩</div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-display text-sm font-black">Extension Chrome « Voamada Importer »</p>
          <p className="text-xs text-white/60 leading-relaxed mt-0.5">
            Scrape <strong>toutes les vidéos d'un profil</strong> TikTok, Instagram ou Facebook en 1 clic depuis ton Chrome.
            100 % gratuit, pas d'API tierce, installe-la en 2 minutes.
          </p>
        </div>
        <a
          href="/voamada-importer-extension.zip"
          download
          className="btn-primary text-xs inline-flex items-center gap-1.5"
          data-testid="chrome-ext-download"
        >
          📥 Télécharger .zip
        </a>
        <button
          type="button"
          onClick={() => window.alert(
            "Installation en 4 étapes :\n\n" +
            "1. Dézippe voamada-importer-extension.zip sur ton bureau\n" +
            "2. Ouvre Chrome et tape : chrome://extensions/\n" +
            "3. Active « Mode développeur » (toggle en haut à droite)\n" +
            "4. Clique « Charger l'extension non empaquetée » et sélectionne le dossier dézippé\n\n" +
            "L'icône Voamada apparaît dans ta barre Chrome. Clique-la sur un profil TikTok/Instagram/Facebook, choisis l'artiste, Envoyer. Terminé."
          )}
          className="btn-ghost text-xs"
          data-testid="chrome-ext-help"
        >
          📖 Guide
        </button>
      </div>

      {/* Artist picker */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4">
        <div className="flex items-center gap-2 mb-3">
          <User size={13} className="text-brand-yellow" />
          <h4 className="font-display text-sm font-black uppercase tracking-wider">Artiste cible</h4>
        </div>

        {selectedArtist ? (
          <div className="rounded-lg border border-brand-yellow/30 bg-brand-yellow/5 p-2.5 flex items-center gap-2" data-testid="tiktok-selected-artist">
            {selectedArtist.picture ? (
              <img src={selectedArtist.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-sm">
                {(selectedArtist.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{selectedArtist.name}</p>
              <p className="text-[10px] text-white/50 truncate">{selectedArtist.country || "—"} · {selectedArtist.tracks_count || 0} tracks</p>
            </div>
            <button onClick={() => setSelectedArtist(null)} className="text-[10px] text-white/50 hover:text-white" data-testid="tiktok-clear-artist">
              Changer
            </button>
          </div>
        ) : (
          <>
            <div className="relative mb-2">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={artistQ}
                onChange={(e) => setArtistQ(e.target.value)}
                placeholder="Rechercher un artiste…"
                className="w-full rounded-lg bg-white/[0.03] border border-white/10 pl-7 pr-3 py-2 text-xs focus:outline-none focus:border-brand-yellow/60"
                data-testid="tiktok-artist-search"
              />
            </div>
            <ul className="max-h-60 overflow-y-auto space-y-1 pr-1">
              {filteredArtists.map((a) => (
                <li key={a.user_id}>
                  <button
                    onClick={() => setSelectedArtist(a)}
                    className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/[0.05] text-left transition-colors"
                    data-testid={`tiktok-artist-pick-${a.user_id}`}
                  >
                    {a.picture ? (
                      <img src={a.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-[10px] shrink-0">
                        {(a.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{a.name}</p>
                      <p className="text-[9px] text-white/40 truncate">{a.country || "—"} · {a.tracks_count || 0} tracks</p>
                    </div>
                  </button>
                </li>
              ))}
              {filteredArtists.length === 0 && (
                <li className="text-xs text-white/40 text-center py-4">Aucun artiste trouvé.</li>
              )}
            </ul>
          </>
        )}
      </div>

      {/* URLs textarea + launch */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Music size={13} className="text-brand-yellow" />
          <h4 className="font-display text-sm font-black uppercase tracking-wider">URLs de vidéos TikTok</h4>
          <span className="ml-auto text-[11px] text-white/50" data-testid="tiktok-url-count">
            {urlList.length} URL{urlList.length > 1 ? "s" : ""} détectée{urlList.length > 1 ? "s" : ""}
          </span>
        </div>

        <textarea
          value={urlsText}
          onChange={(e) => setUrlsText(e.target.value)}
          rows={9}
          placeholder={`Colle une URL par ligne, ex :\nhttps://www.tiktok.com/@rossymada/video/7123456789012345678\nhttps://www.tiktok.com/@bigmjofficiel/video/7234567890123456789\nhttps://www.tiktok.com/@khaby.lame/video/7345678901234567890`}
          className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand-yellow/60 resize-y"
          data-testid="tiktok-urls-textarea"
        />

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={launchImport}
            disabled={running || !selectedArtist || urlList.length === 0}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="tiktok-import-btn"
          >
            {running
              ? <><Loader2 size={14} className="animate-spin" /> Import en cours…</>
              : <><Upload size={14} /> Importer {urlList.length > 0 ? `(${urlList.length})` : ""}</>}
          </button>
          {!selectedArtist && (
            <p className="text-xs text-white/40">← Choisis d'abord un artiste dans la colonne de gauche</p>
          )}
          {selectedArtist && urlList.length === 0 && (
            <p className="text-xs text-white/40">Colle au moins une URL TikTok ci-dessus</p>
          )}
        </div>

        {/* How-to mini */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-3 text-xs text-white/60 leading-relaxed">
          <p className="font-bold text-white/80 mb-1">💡 Comment récupérer l'URL d'une vidéo TikTok ?</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Ouvre la vidéo sur <code className="bg-black/40 px-1.5 py-0.5 rounded text-brand-yellow">tiktok.com</code></li>
            <li>Copie l'URL depuis la barre d'adresse (<kbd className="bg-white/10 border border-white/20 rounded px-1 py-0.5 font-mono text-[10px]">Ctrl+L</kbd> puis <kbd className="bg-white/10 border border-white/20 rounded px-1 py-0.5 font-mono text-[10px]">Ctrl+C</kbd>)</li>
            <li>Colle ici, répète pour chaque vidéo, puis clique <strong>Importer</strong></li>
          </ol>
          <p className="mt-2 text-emerald-300/80">✓ Les titres et miniatures arrivent automatiquement — rien à taper à la main.</p>
        </div>

        {result && (
          <div className="rounded-lg border border-white/10 bg-black/40 p-3 space-y-2" data-testid="tiktok-result">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1.5 text-emerald-400">
                <CheckCircle2 size={14} />
                <span className="font-bold">{result.summary.succeeded}</span>
                <span className="text-xs text-white/60">ajoutée{result.summary.succeeded > 1 ? "s" : ""}</span>
              </div>
              {result.summary.skipped > 0 && (
                <div className="inline-flex items-center gap-1.5 text-white/60">
                  <SkipForward size={14} />
                  <span className="font-bold">{result.summary.skipped}</span>
                  <span className="text-xs">déjà importée{result.summary.skipped > 1 ? "s" : ""}</span>
                </div>
              )}
              {result.summary.failed > 0 && (
                <div className="inline-flex items-center gap-1.5 text-red-400">
                  <XCircle size={14} />
                  <span className="font-bold">{result.summary.failed}</span>
                  <span className="text-xs text-white/60">échec{result.summary.failed > 1 ? "s" : ""}</span>
                </div>
              )}
              <span className="text-[11px] text-white/40 ml-auto">sur {result.summary.total}</span>
            </div>

            {result.created?.length > 0 && (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {result.created.map((c) => (
                  <li key={c.track_id} className="flex items-center gap-2 text-xs text-white/70">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0" />
                    <span className="truncate flex-1">{c.title}</span>
                    <span className="text-[10px] uppercase text-brand-yellow">{c.provider}</span>
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-brand-yellow">
                      <ExternalLink size={10} />
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {result.failed?.length > 0 && (
              <ul className="space-y-1 max-h-24 overflow-y-auto">
                {result.failed.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-white/60">
                    <XCircle size={11} className="text-red-400 shrink-0" />
                    <span className="truncate flex-1 font-mono">{f.url}</span>
                    <span className="text-[10px] text-red-300">{f.error}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkSeedPanel;
